import { native } from '@forest/native-services'
import {
  paymentSystemIdByPlatform,
  SERVICE_LEVEL_POLL_TIMEOUT_MS as PURCHASE_RECONCILIATION_TIMEOUT_MS,
  type PaymentPlatform,
} from '@forest/paywall-core'
import isNetworkError from 'is-network-error'
import { logNonFatal } from '~/lib/diagnostics'
import { isHTTPError, isTimeoutError } from 'ky'
import { api } from './api'

interface CreatePurchaseResponse {
  results: { purchase: { id: number } }[]
}

interface PurchaseStatusResponse {
  purchase?: { status?: unknown }
}

const PURCHASE_RECONCILIATION_INTERVAL_MS = 1_000
const DELIVERY_CONFIRMATION_INTERVAL_MS = 1_000
const DELIVERY_CONFIRMATION_TIMEOUT_MS = 30_000

/**
 * Any Store product bought through the Shop IAP orchestration: the Store
 * product id and the platform SKU it is sold under (GLOSSARY.md: Shop Time
 * Crystal purchase; a starter pack reuses the same orchestration).
 */
export interface IapProduct {
  productId: number
  sku: string
}

export type IapPurchaseFailure =
  | { kind: 'cancelled' }
  | { kind: 'confirmation_pending'; purchaseId: number }
  | { code: string; kind: 'connection' }
  | { kind: 'delivery'; purchaseId: number }
  | { code: string; kind: 'rejected' }

export class IapPurchaseError extends Error {
  private constructor(public readonly failure: IapPurchaseFailure) {
    super('code' in failure ? failure.code : failure.kind)
    this.name = 'IapPurchaseError'
  }

  static cancelled() {
    return new IapPurchaseError({ kind: 'cancelled' })
  }

  static confirmationPending(purchaseId: number) {
    return new IapPurchaseError({
      kind: 'confirmation_pending',
      purchaseId,
    })
  }

  static connection(code: string) {
    return new IapPurchaseError({ code, kind: 'connection' })
  }

  static delivery(purchaseId: number) {
    return new IapPurchaseError({ kind: 'delivery', purchaseId })
  }

  static rejected(code: string) {
    return new IapPurchaseError({ code, kind: 'rejected' })
  }
}

interface IapPurchaseInput {
  // The non-fatal diagnostic a failed native closeTransaction logs, one per
  // kind of product: shop_time_crystal_close_transaction_failed,
  // shop_starter_pack_close_transaction_failed.
  closeTransactionFailureEvent: string
  // The non-fatal diagnostic a purchase still undelivered at the delivery
  // confirmation deadline logs: shop_time_crystal_delivery_timeout,
  // shop_starter_pack_delivery_timeout.
  deliveryTimeoutEvent: string
  platform: PaymentPlatform
  product: IapProduct
  userId: number
}

/**
 * Buys one IAP product: create the Service-API purchase, run the native
 * purchase, mark it processing, submit the receipt, close the native
 * transaction, then wait for delivery — with one replacement purchase on a
 * receipt-service 409 and China Android interrupted reconciliation.
 */
export async function purchaseIapProduct(
  input: IapPurchaseInput,
): Promise<void> {
  const { sku } = input.product
  let purchaseId: number

  try {
    const response = await api.service
      .post(`store/users/${input.userId}/purchase`, {
        json: {
          iap_sku: sku,
          product_id: input.product.productId,
          quantity: 1,
        },
      })
      .json<CreatePurchaseResponse>()
    purchaseId = response.results[0]!.purchase.id
  } catch (error) {
    const code = errorCode(error)
    throw isConnectionError(error)
      ? IapPurchaseError.connection(code)
      : IapPurchaseError.rejected(code)
  }

  const purchasePayload = {
    product_id: purchaseId,
    product_type: 'consumable',
    receipt_system_account_id: null,
    sku,
  } as const
  let initialResult: Awaited<ReturnType<typeof native.iap.purchase>>
  try {
    initialResult = await native.iap.purchase(purchasePayload)
  } catch (error) {
    bestEffortMark(input.userId, purchaseId, 'failed')
    throw IapPurchaseError.rejected(errorCode(error))
  }
  if (!initialResult.success) {
    if (
      initialResult.error === 'interrupted' &&
      input.platform === 'china_android'
    ) {
      const completed = await reconcilePurchase(input.userId, purchaseId)
      if (completed) return
      throw IapPurchaseError.confirmationPending(purchaseId)
    }
    const cancelled = initialResult.error === 'interrupted'
    bestEffortMark(input.userId, purchaseId, cancelled ? 'cancelled' : 'failed')
    throw cancelled
      ? IapPurchaseError.cancelled()
      : IapPurchaseError.rejected(initialResult.error)
  }
  let transactionId = initialResult.data.transaction_id

  try {
    await api.service.post(
      `store/users/${input.userId}/purchase/${purchaseId}/mark-processing`,
    )
  } catch {
    throw IapPurchaseError.delivery(purchaseId)
  }

  let receiptAcceptedAt: number
  try {
    await postReceipt(input, transactionId)
    receiptAcceptedAt = Date.now()
  } catch (error) {
    if (!isHTTPError(error) || error.response.status !== 409) {
      throw IapPurchaseError.delivery(purchaseId)
    }

    await bestEffortCloseTransaction(input, purchaseId, transactionId)

    let replacementResult: Awaited<ReturnType<typeof native.iap.purchase>>
    try {
      replacementResult = await native.iap.purchase(purchasePayload)
    } catch {
      throw IapPurchaseError.delivery(purchaseId)
    }
    if (!replacementResult.success) {
      throw IapPurchaseError.delivery(purchaseId)
    }
    transactionId = replacementResult.data.transaction_id

    try {
      await postReceipt(input, transactionId)
      receiptAcceptedAt = Date.now()
    } catch {
      throw IapPurchaseError.delivery(purchaseId)
    }
  }

  await bestEffortCloseTransaction(input, purchaseId, transactionId)

  const delivered = await confirmDelivery(
    input.userId,
    purchaseId,
    receiptAcceptedAt + DELIVERY_CONFIRMATION_TIMEOUT_MS,
  )
  if (!delivered) {
    logNonFatal(input.deliveryTimeoutEvent, {
      purchase_id: String(purchaseId),
      sku,
    })
    throw IapPurchaseError.confirmationPending(purchaseId)
  }
}

// The receipt service only queues the receipt; the product is delivered once
// the Service-API purchase completes (GLOSSARY.md "IAP delivery
// confirmation").
async function confirmDelivery(
  userId: number,
  purchaseId: number,
  deadline: number,
) {
  while (Date.now() < deadline) {
    try {
      const response = await api.service
        .get(`store/users/${userId}/purchase/${purchaseId}`, {
          retry: 0,
          timeout: deadline - Date.now(),
        })
        .json<PurchaseStatusResponse>()
      if (response.purchase?.status === 'completed') return true
    } catch {
      // A failed status request is not a delivery verdict; keep polling.
    }
    const wait = Math.min(
      DELIVERY_CONFIRMATION_INTERVAL_MS,
      deadline - Date.now(),
    )
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait))
  }
  return false
}

async function reconcilePurchase(userId: number, purchaseId: number) {
  const controller = new AbortController()
  const timeoutId = setTimeout(
    () => controller.abort(),
    PURCHASE_RECONCILIATION_TIMEOUT_MS,
  )

  try {
    while (true) {
      const response = await api.service
        .get(`store/users/${userId}/purchase/${purchaseId}`, {
          retry: 0,
          signal: controller.signal,
        })
        .json<PurchaseStatusResponse>()
      if (response.purchase?.status === 'completed') return true
      await sleep(PURCHASE_RECONCILIATION_INTERVAL_MS, controller.signal)
    }
  } catch {
    return false
  } finally {
    clearTimeout(timeoutId)
  }
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Purchase reconciliation aborted'))
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(new Error('Purchase reconciliation aborted'))
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

async function postReceipt(input: IapPurchaseInput, transactionId: string) {
  await api.receipt.post(
    `projects/6/users/${input.userId}/pending_iap_receipts`,
    {
      json: {
        payment_system_id: paymentSystemIdByPlatform[input.platform],
        receipt_data: transactionId,
        sku_id: input.product.sku,
      },
    },
  )
}

async function bestEffortCloseTransaction(
  input: IapPurchaseInput,
  purchaseId: number,
  transactionId: string,
) {
  try {
    await native.iap.closeTransaction({ transaction_id: transactionId })
  } catch (error) {
    logNonFatal(input.closeTransactionFailureEvent, {
      message: error instanceof Error ? error.message : String(error),
      purchase_id: String(purchaseId),
      sku: input.product.sku,
      transaction_id: transactionId,
    })
  }
}

function bestEffortMark(
  userId: number,
  purchaseId: number,
  state: 'cancelled' | 'failed',
) {
  try {
    void api.service
      .post(`store/users/${userId}/purchase/${purchaseId}/mark-${state}`)
      .catch(() => {})
  } catch {
    // A terminal mark is advisory cleanup and must not replace the real result.
  }
}

function isConnectionError(error: unknown) {
  return (
    isTimeoutError(error) ||
    (isNetworkError as (candidate: unknown) => boolean)(error)
  )
}

function errorCode(error: unknown) {
  if (isHTTPError(error)) return String(error.response.status)
  return error instanceof Error ? error.message : String(error)
}
